import { jsx } from 'react/jsx-runtime';
import { Suspense, Children, createContext, useReducer, useCallback, useContext } from 'react';

const Route = ({
  children,
  fallback
}) => {
  return /* @__PURE__ */ jsx(Suspense, { fallback, children });
};

const removePrefix = (current, prefix) => {
  if (prefix?.length) {
    return current.substring(prefix.length + 1);
  }
  return current;
};
const routeFilter = (route) => route.type === Route;
const toRouteArray = (children) => Children.toArray(children).filter(routeFilter);
const extractRoutePaths = (routes) => routes.map((route) => route.props.path);
const findRouteByPath = (routes, path) => routes.find((route) => route.props.path === path);
const pathMatcher = (path, current) => path.endsWith("/*") && current.startsWith(path.substring(0, path.length - 1));
const findRouteByWildcard = (routes, path) => routes.find((route) => pathMatcher(route.props.path, path));
const findRoute = (routes, path) => findRouteByPath(routes, path) || findRouteByWildcard(routes, path);
const createRoutes = (routes) => {
  return routes.map((route) => /* @__PURE__ */ jsx(
    Route,
    {
      path: route.path,
      fallback: route.fallback,
      children: route.element
    },
    route.path
  ));
};

const Router = ({ current, prefix, children }) => {
  const routeToDisplay = findRoute(
    toRouteArray(children),
    removePrefix(current, prefix)
  );
  if (!routeToDisplay) {
    const withPrefix = prefix?.length ? `, with prefix [${prefix}]` : "";
    const availableRoutes = extractRoutePaths(toRouteArray(children));
    throw Error(`No route found for path [${current}]${withPrefix}, available routes are [${availableRoutes}]!`);
  }
  return routeToDisplay;
};

const routerReducer = (state, action) => {
  if (action.type === "PUSH") {
    return {
      currentStep: action.payload,
      history: [action.payload, ...state.history]
    };
  }
  if (action.type === "REPLACE") {
    return {
      currentStep: action.payload,
      history: [action.payload, ...state.history.slice(1)]
    };
  }
  if (action.type === "GOBACK") {
    if (typeof action.payload === "number") {
      const currentStep = state.history.at(action.payload);
      if (!currentStep) {
        throw new Error("Router cannot go back");
      }
      return {
        currentStep,
        history: state.history.slice(action.payload)
      };
    }
    const stepPosition = state.history.findIndex((step) => step === action.payload);
    if (stepPosition === -1) {
      throw new Error(`${action.payload} doesn't exist in history`);
    }
    return {
      currentStep: action.payload,
      history: state.history.slice(stepPosition)
    };
  }
  return state;
};

const RouterContext = createContext(null);
const RouterProvider = ({ initialStep, prefix, history, children }) => {
  const [routerState, dispatchRouterAction] = useReducer(
    routerReducer,
    { currentStep: initialStep, history: history ?? [initialStep] }
  );
  const push = useCallback((step) => {
    dispatchRouterAction({
      type: "PUSH",
      payload: step
    });
  }, []);
  const replace = useCallback((step) => {
    dispatchRouterAction({
      type: "REPLACE",
      payload: step
    });
  }, []);
  const goBack = useCallback((step) => {
    dispatchRouterAction({
      type: "GOBACK",
      payload: step ?? 1
    });
  }, []);
  return /* @__PURE__ */ jsx(
    RouterContext.Provider,
    {
      value: {
        ...routerState,
        prefix,
        push,
        replace,
        goBack
      },
      children
    }
  );
};
const useNavigation = () => {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error("useNavigation must be used within a Router");
  }
  return context;
};

const Routes = ({ children }) => {
  const { currentStep, prefix } = useNavigation();
  return /* @__PURE__ */ jsx(Router, { current: currentStep, prefix, children });
};

export { Route, Router, RouterContext, RouterProvider, Routes, createRoutes, extractRoutePaths, findRoute, findRouteByPath, findRouteByWildcard, pathMatcher, removePrefix, toRouteArray, useNavigation };
